/**
 * SPA wiring: camera / upload → pose engine → torso metrics → coaching cues.
 */

import { PoseEngine, type FrameAnalysis } from "../tracking/pose-engine";
import { drawAnalysis } from "./renderer";
import {
  syntheticHandstandLandmarks,
  computePoseMetrics,
  torsoFromLandmarks,
} from "../biomechanics";
import type { CoachingCue } from "../biomechanics/types";
import { coachFromMetrics, getKnowledge } from "../biomechanics/coaching";

export function mountApp(root: HTMLElement): void {
  const kb = getKnowledge();

  root.innerHTML = `
    <div class="shell">
      <header class="hero">
        <div>
          <p class="eyebrow">Feral Handstands</p>
          <h1>Automated handstand coaching</h1>
          <p class="lede">
            Live pose tracking densifies the torso <strong>between shoulders and hips</strong>
            so banana-back vs hollow stack is measurable — cues grounded in Balanced Notion
            curriculum + Zoom coaching corpus.
          </p>
        </div>
        <div class="hero-stats">
          <div><span class="n">${kb.techniques.length}</span><span class="l">techniques mapped</span></div>
          <div><span class="n">${kb.cues.length}</span><span class="l">coaching cues</span></div>
          <div><span class="n">${kb.commonMistakes.length}</span><span class="l">common mistakes</span></div>
        </div>
      </header>

      <section class="stage-grid">
        <div class="stage-panel">
          <div class="stage-wrap">
            <video id="input-video" playsinline muted></video>
            <canvas id="overlay"></canvas>
            <div class="stage-badge" id="stage-badge">Idle</div>
          </div>
          <div class="controls">
            <button type="button" id="btn-camera" class="btn primary">Start camera</button>
            <button type="button" id="btn-stop" class="btn" disabled>Stop</button>
            <label class="btn file-btn">
              Upload video
              <input type="file" id="file-input" accept="video/*" hidden />
            </label>
            <button type="button" id="btn-demo" class="btn">Demo pose</button>
            <button type="button" id="btn-banana" class="btn ghost">Demo banana</button>
          </div>
          <p class="hint" id="status-line">
            Camera or upload runs entirely in-browser (MediaPipe). No video leaves your device.
          </p>
        </div>

        <aside class="side">
          <section class="card" id="cue-card">
            <h2>Coaching</h2>
            <p class="primary-cue" id="primary-cue">Start camera or load a demo pose.</p>
            <ul id="cue-list" class="cue-list"></ul>
          </section>

          <section class="card">
            <h2>Torso metrics</h2>
            <p class="metric-note">Intermediate points between shoulders &amp; hips drive stack / hollow scores.</p>
            <div class="metrics" id="metrics-grid"></div>
          </section>

          <section class="card">
            <h2>Curriculum themes</h2>
            <ul class="theme-list">
              <li><strong>Alignment</strong> — ${kb.themes.alignment.summary}</li>
              <li><strong>Balance</strong> — ${kb.themes.balance.summary}</li>
              <li><strong>Isolation</strong> — ${kb.themes.isolation.summary}</li>
              <li><strong>AIB</strong> — ${kb.themes.aib.summary}</li>
            </ul>
          </section>
        </aside>
      </section>

      <section class="card knowledge">
        <h2>Alignment checkpoints</h2>
        <ol class="check-list" id="checkpoint-list"></ol>
        <h2>Mapped techniques (sample)</h2>
        <div class="tech-grid" id="tech-grid"></div>
      </section>

      <footer class="footer">
        <p>
          Cloudflare Pages ready · <code>npm run build</code> → <code>dist/</code> ·
          client-side only · source videos not redistributed
        </p>
      </footer>
    </div>
  `;

  const checkpointList = root.querySelector("#checkpoint-list")!;
  checkpointList.innerHTML = kb.alignmentCheckpoints
    .map((c) => `<li><strong>${c.name}</strong> — ${c.description}</li>`)
    .join("");

  const techGrid = root.querySelector("#tech-grid")!;
  techGrid.innerHTML = kb.techniques
    .slice(0, 24)
    .map(
      (t) =>
        `<div class="tech"><span class="tag">${t.level}</span><span>${t.name}</span><span class="cat">${t.category}</span></div>`,
    )
    .join("");

  const video = root.querySelector<HTMLVideoElement>("#input-video")!;
  const canvas = root.querySelector<HTMLCanvasElement>("#overlay")!;
  const ctx = canvas.getContext("2d")!;
  const badge = root.querySelector("#stage-badge")!;
  const statusLine = root.querySelector("#status-line")!;
  const primaryCueEl = root.querySelector("#primary-cue")!;
  const cueList = root.querySelector("#cue-list")!;
  const metricsGrid = root.querySelector("#metrics-grid")!;
  const btnCamera = root.querySelector<HTMLButtonElement>("#btn-camera")!;
  const btnStop = root.querySelector<HTMLButtonElement>("#btn-stop")!;
  const btnDemo = root.querySelector<HTMLButtonElement>("#btn-demo")!;
  const btnBanana = root.querySelector<HTMLButtonElement>("#btn-banana")!;
  const fileInput = root.querySelector<HTMLInputElement>("#file-input")!;

  const engine = new PoseEngine();
  let raf = 0;
  let stream: MediaStream | null = null;
  let running = false;
  let demoMode: null | "good" | "banana" = null;
  function setMirror(on: boolean): void {
    // Front-camera selfie: flip video + canvas together so overlay stays locked.
    video.classList.toggle("mirror-x", on);
    canvas.classList.toggle("mirror-x", on);
  }

  /** True when the active track is a user-facing (selfie) camera. */
  function isFrontFacingCamera(media: MediaStream): boolean {
    const track = media.getVideoTracks()[0];
    if (!track) return true;
    const facing = track.getSettings().facingMode;
    // Desktop often omits facingMode; we requested facingMode: "user".
    if (!facing) return true;
    return facing === "user" || facing === "front";
  }

  void engine.init().then(() => {
    if (engine.isReady) {
      statusLine.textContent =
        "Pose model ready. Start camera or upload a video — torso intermediates densify live.";
    } else {
      statusLine.textContent = `Pose model offline (${engine.error ?? "unknown"}). Demo poses still exercise the full coaching pipeline.`;
    }
  });

  function resizeCanvas(): void {
    const rect = video.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
  }

  function renderHud(analysis: FrameAnalysis): void {
    const m = analysis.metrics;
    const midCount = m.intermediateMidline.length;
    metricsGrid.innerHTML = [
      metricHtml("Mid points", String(midCount), "between sh↔hip"),
      metricHtml("Banana idx", m.bananaIndex.toFixed(3), "+ arch / − hollow"),
      metricHtml("Stack dev", m.stackDeviation.toFixed(3), "mean |offset|"),
      metricHtml("Shoulder open", pct(m.shoulderOpen), ""),
      metricHtml("Elbow lock", pct(m.elbowLock), ""),
      metricHtml("Inverted", pct(m.inverted), ""),
      metricHtml("Quality", pct(m.overallQuality), ""),
      metricHtml("Stack ∠", `${m.stackAngleDeg.toFixed(1)}°`, ""),
    ].join("");

    const primary = analysis.coaching.primaryCue;
    primaryCueEl.textContent = primary?.text ?? "Hold a clear side or front handstand view.";
    primaryCueEl.setAttribute("data-cue-id", primary?.id ?? "");
    cueList.innerHTML = analysis.coaching.cues
      .map(
        (c: CoachingCue) =>
          `<li data-cue-id="${c.id}"><span class="cue-id">${c.id}</span> ${c.text}</li>`,
      )
      .join("");

    // Expose last analysis for headless verification
    (window as unknown as { __feralLastAnalysis?: FrameAnalysis }).__feralLastAnalysis =
      analysis;
  }

  function metricHtml(label: string, value: string, hint: string): string {
    return `<div class="metric" data-metric="${label}"><div class="mv">${value}</div><div class="ml">${label}</div>${hint ? `<div class="mh">${hint}</div>` : ""}</div>`;
  }

  function pct(v: number): string {
    return `${Math.round(v * 100)}%`;
  }

  function showAnalysis(analysis: FrameAnalysis): void {
    resizeCanvas();
    const isDemo = demoMode !== null;
    const hasLiveVideo = !isDemo && video.readyState >= 2;

    // Always wipe bitmap first. Opaque fill only for demo (no video underneath).
    // Live camera/upload must stay transparent so the <video> shows through.
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!hasLiveVideo) {
      ctx.fillStyle = "#0b1016";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    const vw = video.videoWidth || canvas.width;
    const vh = video.videoHeight || canvas.height;
    drawAnalysis(ctx, analysis, {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      videoWidth: isDemo ? canvas.width : vw,
      videoHeight: isDemo ? canvas.height : vh,
      fit: "contain",
      identityLayout: isDemo,
      // When CSS mirrors both video + canvas, do not double-flip landmarks.
      mirrorX: false,
    });
    renderHud(analysis);
    badge.textContent =
      analysis.metrics.inverted > 0.45 ? "Inverted · tracking" : "Tracking";
  }

  function showIdleStage(): void {
    resizeCanvas();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#0b1016";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(154, 171, 188, 0.9)";
    ctx.font = "600 14px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Start camera or upload a video to track",
      canvas.width / 2,
      canvas.height / 2 - 8,
    );
    ctx.fillStyle = "rgba(154, 171, 188, 0.55)";
    ctx.font = "13px system-ui, sans-serif";
    ctx.fillText(
      "Demo pose / Demo banana preview the coaching overlay",
      canvas.width / 2,
      canvas.height / 2 + 16,
    );
    badge.textContent = "Idle";
  }

  function tick(): void {
    if (!running) return;
    if (demoMode) {
      const banana = demoMode === "banana" ? 0.12 : 0;
      const lm = syntheticHandstandLandmarks({ banana });
      // Force explicit curvature on the densified torso for visible banana demo
      const torso = torsoFromLandmarks(lm, {
        intermediateCount: 5,
        // Good demo: collinear midpoints on the body line. Banana: visible arch.
        spineCurvature: demoMode === "banana" ? 0.1 : 0,
      });
      const metrics = computePoseMetrics(lm, { torso });
      const coaching = coachFromMetrics(metrics);
      showAnalysis({
        landmarks: lm,
        torso,
        metrics,
        coaching,
        timestampMs: performance.now(),
      });
      raf = requestAnimationFrame(tick);
      return;
    }

    if (video.readyState >= 2 && engine.isReady) {
      const analysis = engine.analyzeVideoFrame(video, performance.now());
      if (analysis) showAnalysis(analysis);
    }
    raf = requestAnimationFrame(tick);
  }

  async function startCamera(): Promise<void> {
    stopAll(false);
    demoMode = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      // Front-facing only: mirror video + overlay so left/right match a mirror.
      // MediaPipe still sees the unmirrored frame; CSS flips both layers together.
      const front = isFrontFacingCamera(stream);
      setMirror(front);
      video.srcObject = stream;
      await video.play();
      running = true;
      btnStop.disabled = false;
      badge.textContent = front ? "Camera · mirrored" : "Camera";
      statusLine.textContent = engine.isReady
        ? front
          ? "Front camera live (mirrored) — tracking locked to the mirror view."
          : "Camera live — densifying torso intermediates each frame."
        : "Camera live but pose model not ready; try Demo pose for coaching pipeline.";
      raf = requestAnimationFrame(tick);
    } catch (err) {
      statusLine.textContent = `Camera error: ${err instanceof Error ? err.message : String(err)}`;
    }
  }

  function startDemo(mode: "good" | "banana"): void {
    stopAll(false);
    demoMode = mode;
    setMirror(false);
    running = true;
    btnStop.disabled = false;
    video.pause();
    video.removeAttribute("src");
    video.srcObject = null;
    video.load();
    badge.textContent = mode === "banana" ? "Demo · banana" : "Demo · stack";
    statusLine.textContent =
      mode === "banana"
        ? "Synthetic banana-back: intermediate midline points leave the shoulder–hip chord."
        : "Synthetic straight-stack handstand — mid-torso points on the shoulder–hip line.";
    raf = requestAnimationFrame(tick);
  }

  function stopAll(resetBadge = true): void {
    running = false;
    cancelAnimationFrame(raf);
    if (stream) {
      for (const t of stream.getTracks()) t.stop();
      stream = null;
    }
    video.pause();
    if (video.src && video.src.startsWith("blob:")) {
      URL.revokeObjectURL(video.src);
    }
    video.removeAttribute("src");
    video.srcObject = null;
    setMirror(false);
    demoMode = null;
    btnStop.disabled = true;
    if (resetBadge) {
      badge.textContent = "Idle";
      showIdleStage();
    }
  }

  btnCamera.addEventListener("click", () => void startCamera());
  btnStop.addEventListener("click", () => {
    stopAll();
    statusLine.textContent = "Stopped.";
  });
  btnDemo.addEventListener("click", () => startDemo("good"));
  btnBanana.addEventListener("click", () => startDemo("banana"));

  fileInput.addEventListener("change", async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    stopAll(false);
    demoMode = null;
    setMirror(false);
    const url = URL.createObjectURL(file);
    video.srcObject = null;
    video.src = url;
    video.loop = true;
    await video.play();
    running = true;
    btnStop.disabled = false;
    badge.textContent = "Upload";
    statusLine.textContent = `Playing ${file.name}`;
    raf = requestAnimationFrame(tick);
  });

  window.addEventListener("resize", () => {
    if (running) resizeCanvas();
    else showIdleStage();
  });

  // Idle landing (no crooked auto-demo). Headless tests call __feralStartDemo.
  requestAnimationFrame(() => showIdleStage());

  // Public test hooks
  (window as unknown as { __feralEngine?: PoseEngine }).__feralEngine = engine;
  (window as unknown as { __feralStartDemo?: (m: "good" | "banana") => void }).__feralStartDemo =
    startDemo;
  (window as unknown as { __feralShowIdle?: () => void }).__feralShowIdle = showIdleStage;
}
